import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

// Isosceles Triangle Construction
export class IsoscelesTriangleConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; base?: number; leg?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 220;
    const startY = options.y ?? 480;
    const baseLen = options.base ?? 260;
    const legLen = options.leg ?? 200;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + baseLen, y: startY };
    const h = Math.sqrt(legLen * legLen - (baseLen / 2) * (baseLen / 2));
    const pC = { x: startX + baseLen / 2, y: startY - h };

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');

    const segAB = new Segment(this.renderer, pA, pB, false);
    const segAC = new Segment(this.renderer, pA, pC, false);
    const segBC = new Segment(this.renderer, pB, pC, false);

    const arcA = new Arc(this.renderer, pA, legLen, -Math.PI * 0.2, -Math.PI * 0.45, true, false);
    const arcB = new Arc(this.renderer, pB, legLen, -Math.PI * 0.55, -Math.PI * 0.8, true, false);

    [pointA, pointB, pointC, segAB, segAC, segBC, arcA, arcB].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Base Segment AB',
        description: 'Draw base segment AB.',
        narration: 'Step 1: Drawing base segment AB of the isosceles triangle.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(segAB, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Intersect Arcs of Equal Leg Length',
        description: 'Draw arcs centered at A and B with equal leg radius to find vertex C.',
        narration: 'Step 2: Drawing equal radius arcs from A and B to locate apex vertex C.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pA, legLen),
          new DrawArc(arcA, this.compass),
          new MoveCompass(this.compass, pB, legLen),
          new DrawArc(arcB, this.compass),
          new DrawPoint(pointC),
        ],
      },
      {
        id: 'step3',
        title: '3. Connect Sides AC and BC',
        description: 'Use ruler to connect A to C and B to C.',
        narration: 'Construction complete! Isosceles triangle ABC constructed with equal sides AC = BC.',
        duration: 1800,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(segAC, this.ruler),
          new DrawLine(segBC, this.ruler),
          new Highlight(segAC),
          new Highlight(segBC),
        ],
      },
    ];
  }
}

// Right Triangle Construction
export class RightTriangleConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; base?: number; height?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 220;
    const startY = options.y ?? 480;
    const baseLen = options.base ?? 250;
    const height = options.height ?? 180;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + baseLen, y: startY };
    const pC = { x: startX, y: startY - height };

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');

    const segAB = new Segment(this.renderer, pA, pB, false);
    const segAC = new Segment(this.renderer, pA, pC, false);
    const segBC = new Segment(this.renderer, pB, pC, false);
    const rightAngle = new Angle(this.renderer, pA, pB, pC, '90°', true);

    [pointA, pointB, pointC, segAB, segAC, segBC, rightAngle].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Base Segment AB',
        description: 'Draw base segment AB.',
        narration: 'Step 1: Drawing base segment AB.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(segAB, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Construct Perpendicular Side AC',
        description: 'Draw vertical perpendicular side AC of height h.',
        narration: 'Step 2: Drawing 90 degree perpendicular side AC.',
        duration: 1500,
        animations: [new DrawLine(segAC, this.ruler), new DrawPoint(pointC)],
      },
      {
        id: 'step3',
        title: '3. Connect Hypotenuse BC',
        description: 'Connect B to C to complete right-angled triangle.',
        narration: 'Construction complete! Right triangle ABC finished.',
        duration: 1500,
        animations: [new DrawLine(segBC, this.ruler), new DrawPoint(rightAngle as any), new Highlight(segBC)],
      },
    ];
  }
}
